/**
 * SEO tags for one Metal Market Intelligence article, rendered by
 * `src/pages/metal-resources/metal-market-intelligence/[slug].tsx` beside `./Blog.tsx`.
 *
 * Through `components/Shared/model/MetaHead.tsx` it sets the title (`seoTitle`, else `title`),
 * the description (`seoDescription`, else empty), the `og:image` (the hero image) and the
 * canonical path `/metal-resources/metal-market-intelligence/<slug>`. `company: false`, so the
 * Organization JSON-LD is left out. In its place it adds a schema.org `BlogPosting` JSON-LD block
 * with Ryerson as author and publisher. Its `@id` and logo URL are site-relative paths.
 *
 * It builds the tags for all three languages and shows the visitor's. Every language's entry must
 * have a `heroImage`: the `og:image` lookup has no optional chaining, so one without throws on
 * render in the browser. The category pages' tags are in `./BlogCategoryMetaHead.tsx`.
 */
import React from "react";
import MetaHead, { MetaContent } from "@components/Shared/model/MetaHead";
import { TranslatedAllContentfulBlogResponse } from "@contentful/blog/types";
import { useApplication } from "@ryerson/frontend.application";
import { LanguageContent } from "@ryerson/frontend.common";

interface BlogMetaHeadProps {
	translatedBlogNode: TranslatedAllContentfulBlogResponse;
}

/** The article's `<head>` tags and `BlogPosting` JSON-LD; see the file header. */
const BlogMetaHead: React.FC<BlogMetaHeadProps> = ({ translatedBlogNode }) => {
	const {
		localization: { language },
	} = useApplication();

	const AllKeys = Object.keys(translatedBlogNode);
	let TranslatedMetaObject = AllKeys.reduce((acc, langkey) => {
		let translatedBlogKey = langkey as keyof TranslatedAllContentfulBlogResponse;
		acc[translatedBlogKey] = {
			title:
				translatedBlogNode[translatedBlogKey].seoTitle ||
				translatedBlogNode[translatedBlogKey].title,
			description: translatedBlogNode[translatedBlogKey].seoDescription || "",
			url:
				"/metal-resources/metal-market-intelligence/" +
				translatedBlogNode[translatedBlogKey].slug,
			company: false,
			imageUrl: translatedBlogNode[translatedBlogKey].heroImage.file.url || "",
		};
		return acc;
	}, {} as LanguageContent<MetaContent>);

	const structuredContent = {
		"@context": "https://schema.org",
		"@type": "BlogPosting",
		mainEntityOfPage: {
			"@type": "WebPage",
			"@id":
				"/metal-resources/metal-market-intelligence/" +
				(translatedBlogNode[language].slug || ""),
		},
		headline: translatedBlogNode[language].title || "",
		description: translatedBlogNode[language].seoDescription || "",
		image: translatedBlogNode[language].heroImage?.file?.url || "",
		author: {
			"@type": "Organization",
			name: "Ryerson",
		},
		publisher: {
			"@type": "Organization",
			name: "Ryerson",
			logo: {
				"@type": "ImageObject",
				url: "/images/RyersonLogo.png",
			},
		},
		datePublished: translatedBlogNode[language].publishDate || "",
	};

	return (
		<MetaHead content={TranslatedMetaObject[language]}>
			<script type="application/ld+json">{JSON.stringify(structuredContent)}</script>
		</MetaHead>
	);
};

export default BlogMetaHead;
